import { memo } from 'react';
import { Label as RACLabel, LabelProps as RACLabelProps } from 'react-aria-components';

import { ComponentContextProps, merge, renderWithContext, useComponent } from '@/components';

export type LabelProps = RACLabelProps & Partial<ComponentContextProps> & { isRequired?: boolean };

const ILabel = ({ variant, isRequired, className, children, ...rest }: LabelProps) => {
  const context = useComponent({ variant });

  /* prettier-ignore */
  const styles = [
    'font-inter',
    'font-medium',
    'text-xs',
    'flex',
    'gap-xs',

    /* Button */
    'group-[*]/button:font-semibold',

    /* FilterGroup */
    'group-[*]/filter-group:font-medium',
    'group-data-[selected]/filter-group:font-semibold',

    /* ModelPanel */
    'group-[*]/model-panel:text-sm',

    /* MenuItem */
    'group-[*]/menu-item:font-medium',
    'group-data-[selected]/menu-item:font-semibold',

    /* TextField */
    'group-[*]/text-field:text-gray-700',

    /* Field */
    'group-[*]/field-label:text-gray-700',
  ];

  /* prettier-ignore */
  const plainStyles = [
    'text-gray-700',

     /* Button */
    'group-[*]/button:text-gray-600',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-700',
    'group-hover/button:text-gray-700',

    /* MenuItem */
    'group-[*]/menu-item:text-gray-600',
    'group-data-[selected]/menu-item:text-gray-800',

    /* FilterGroup */
    'group-data-[selected]/filter-group:text-primary-600',
  ];

  /* prettier-ignore */
  const primaryStyles = [
    'text-primary-900',

    /* Button */
    'group-[*]/button:text-white',
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-white',
    'group-hover/button:text-white',
  ];

  /* prettier-ignore */
  const secondaryStyles = [
    'text-gray-900',

    /* Button */
    'group-data-[disabled]/button:text-gray-400',
    'group-data-[pressed]/button:text-gray-800',
    'group-hover/button:text-gray-800',
  ];

  /* prettier-ignore */
  const successStyles = [
    'text-success-900',

    /* Button */
    'group-data-[disabled]/button:text-success-500',
    'group-data-[pressed]/button:text-success-700',
    'group-hover/button:text-success-700',
  ];

  /* prettier-ignore */
  const warningStyles = [
    'text-warning-900',

    /* Button */
    'group-data-[disabled]/button:text-warning-500',
    'group-data-[pressed]/button:text-warning-700',
    'group-hover/button:text-warning-700',
  ];

  /* prettier-ignore */
  const errorStyles = [
    'text-error-700',

    /* Button */
    'group-data-[disabled]/button:text-error-500',
    'group-data-[pressed]/button:text-error-700',
    'group-hover/button:text-error-700',
  ];

  return (
    <RACLabel
      className={merge(
        styles,
        context.variant === 'default' && plainStyles,
        context.variant === 'plain' && plainStyles,
        context.variant === 'primary' && primaryStyles,
        context.variant === 'secondary' && secondaryStyles,
        context.variant === 'success' && successStyles,
        context.variant === 'warning' && warningStyles,
        context.variant === 'error' && errorStyles,
        className
      )}
      {...rest}
    >
      {renderWithContext(children, context)}
      {isRequired ? <span className="text-error-500 align-top">*</span> : null}
    </RACLabel>
  );
};

export const Label = memo(ILabel) as typeof ILabel;
